import React from 'react';
import { X, Printer, Package, User, MapPin, CreditCard, Calendar, FileText } from 'lucide-react';
import { formatPrice, formatImageUrl } from '../utils/helpers';

const OrderDetailModal = ({ isOpen, onClose, order }) => {
  if (!isOpen || !order) return null;

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      'processing': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'shipped': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      'delivered': 'bg-green-500/20 text-green-300 border-green-500/30',
      'cancelled': 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-300';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'pending': 'text-yellow-400',
      'paid': 'text-green-400',
      'failed': 'text-red-400',
      'refunded': 'text-gray-400'
    };
    return colors[status] || 'text-gray-400';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasPhysicalProducts = order.items && order.items.length > 0;
  const hasServices = order.service_items && order.service_items.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/90 backdrop-blur-sm print:bg-white">
      <div className="min-h-screen px-4 py-8">
        <div className="max-w-4xl mx-auto bg-white text-black rounded-xl shadow-2xl print:shadow-none">
          {/* Header - Hidden on print */}
          <div className="print:hidden sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-xl flex justify-between items-center">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6" />
              Rendelés Részletei
            </h2>
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition flex items-center gap-2"
              >
                <Printer className="w-5 h-5" />
                Nyomtatás
              </button>
              <button onClick={onClose} className="text-2xl hover:bg-white/20 rounded-lg p-2">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Print Header - Only visible on print */}
          <div className="hidden print:block border-b-2 border-purple-600 pb-6 mb-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-purple-600 mb-2">Célestine - Spirituális bolt</h1>
              <p className="text-gray-600">Rendelés visszaigazolás</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 print:p-8">
            {/* Order Info Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pb-6 border-b-2 border-gray-200">
              <div>
                <p className="text-sm text-gray-500 mb-1">Rendelésszám</p>
                <p className="text-xl font-bold text-purple-600">{order.order_number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Rendelés dátuma</p>
                <p className="font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(order.created_at)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Rendelés állapota</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border print:border-2 ${getStatusColor(order.status)}`}>
                  {order.status === 'pending' && 'Függőben'}
                  {order.status === 'processing' && 'Feldolgozás alatt'}
                  {order.status === 'shipped' && 'Kiszállítva'}
                  {order.status === 'delivered' && 'Kézbesítve'}
                  {order.status === 'cancelled' && 'Törölve'}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Fizetési állapot</p>
                <p className={`font-semibold ${getPaymentStatusColor(order.payment_status)}`}>
                  {order.payment_status === 'pending' && '⏳ Fizetésre vár'}
                  {order.payment_status === 'paid' && '✅ Kifizetve'}
                  {order.payment_status === 'failed' && '❌ Sikertelen'}
                  {order.payment_status === 'refunded' && '↩️ Visszatérítve'}
                </p>
              </div>
            </div>

            {/* Customer & Addresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Customer Info */}
              <div className="border-2 border-gray-200 rounded-lg p-4 print:break-inside-avoid">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-purple-600">
                  <User className="w-5 h-5" />
                  Vásárló adatai
                </h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-semibold">Email:</span> {order.guest_email || order.user_id}</p>
                  {order.shipping_address && (
                    <>
                      <p><span className="font-semibold">Név:</span> {order.shipping_address.name}</p>
                      <p><span className="font-semibold">Telefon:</span> {order.shipping_address.phone}</p>
                    </>
                  )}
                </div>
              </div>

              {/* Shipping Address */}
              {hasPhysicalProducts && order.shipping_address && (
                <div className="border-2 border-gray-200 rounded-lg p-4 print:break-inside-avoid">
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-purple-600">
                    <MapPin className="w-5 h-5" />
                    Szállítási cím
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p className="font-semibold">{order.shipping_address.name}</p>
                    <p>{order.shipping_address.address}</p>
                    <p>{order.shipping_address.city}, {order.shipping_address.zip}</p>
                    <p>{order.shipping_address.country}</p>
                    <p className="text-gray-600">Tel: {order.shipping_address.phone}</p>
                  </div>
                </div>
              )}

              {/* Service-only info */}
              {!hasPhysicalProducts && hasServices && (
                <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-4 print:break-inside-avoid">
                  <h3 className="font-bold text-lg mb-2 flex items-center gap-2 text-blue-600">
                    ✨ Online szolgáltatás
                  </h3>
                  <p className="text-sm text-gray-700">
                    Ez egy online szolgáltatás rendelés - nincs szállítási cím.
                  </p>
                </div>
              )}
            </div>

            {/* Products */}
            {hasPhysicalProducts && (
              <div className="mb-8 print:break-inside-avoid">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-purple-600">
                  <Package className="w-5 h-5" />
                  Termékek
                </h3>
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold">Termék</th>
                        <th className="text-center p-3 text-sm font-semibold">Mennyiség</th>
                        <th className="text-right p-3 text-sm font-semibold">Egységár</th>
                        <th className="text-right p-3 text-sm font-semibold">Összesen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              {item.image && (
                                <img
                                  src={formatImageUrl(item.image)}
                                  alt={item.name}
                                  className="w-12 h-12 object-cover rounded print:w-10 print:h-10"
                                />
                              )}
                              <span className="font-medium">{item.name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-center">{item.quantity} db</td>
                          <td className="p-3 text-right">{formatPrice(item.price)}</td>
                          <td className="p-3 text-right font-semibold">{formatPrice(item.price * item.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Services */}
            {hasServices && (
              <div className="mb-8 print:break-inside-avoid">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-purple-600">
                  ✨ Szolgáltatások
                </h3>
                <div className="space-y-4">
                  {order.service_items.map((item, index) => (
                    <div key={index} className="border-2 border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3 flex-1">
                          {item.image && (
                            <img
                              src={formatImageUrl(item.image)}
                              alt={item.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="font-bold text-lg">{item.name}</p>
                            <p className="text-sm text-gray-600">{item.quantity} alkalom</p>
                          </div>
                        </div>
                        <p className="font-bold text-lg text-purple-600">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                      {(item.full_name || item.birth_date || item.questions) && (
                        <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm">
                          {item.full_name && <p><span className="font-semibold">Név:</span> {item.full_name}</p>}
                          {item.birth_date && <p><span className="font-semibold">Születési dátum:</span> {item.birth_date}</p>}
                          {item.questions && (
                            <div>
                              <p className="font-semibold mb-1">Kérdések/Megjegyzések:</p>
                              <p className="text-gray-700 whitespace-pre-line">{item.questions}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment & Totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Payment Method */}
              <div className="border-2 border-gray-200 rounded-lg p-4 print:break-inside-avoid">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-purple-600">
                  <CreditCard className="w-5 h-5" />
                  Fizetési mód
                </h3>
                <p className="text-sm">
                  {order.payment_method === 'transfer' && 'Banki átutalás'}
                  {order.payment_method === 'barion' && 'Barion (bankkártya)'}
                  {order.payment_method === 'simplepay' && 'SimplePay (OTP)'}
                  {order.payment_method === 'cash' && 'Utánvét / Készpénz'}
                </p>
              </div>

              {/* Order Totals */}
              <div className="border-2 border-purple-200 bg-purple-50 rounded-lg p-4 print:break-inside-avoid">
                <h3 className="font-bold text-lg mb-3 text-purple-600">Összesítő</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Részösszeg:</span>
                    <span className="font-semibold">{formatPrice(order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Szállítási költség:</span>
                    <span className="font-semibold">
                      {order.shipping_cost > 0 ? formatPrice(order.shipping_cost) : 'Ingyenes'}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t-2 border-purple-300 pt-2 mt-2">
                    <span>Végösszeg:</span>
                    <span className="text-purple-600">{formatPrice(order.total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {order.notes && (
              <div className="border-2 border-gray-200 rounded-lg p-4 print:break-inside-avoid">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-purple-600">
                  <FileText className="w-5 h-5" />
                  Megjegyzések
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-line">{order.notes}</p>
              </div>
            )}

            {/* Print Footer */}
            <div className="hidden print:block mt-8 pt-6 border-t-2 border-gray-300 text-center text-sm text-gray-600">
              <p>Köszönjük a vásárlást! ✨</p>
              <p className="mt-2">Célestine - Spirituális bolt | www.celestine.hu</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            background: white !important;
          }
          @page {
            margin: 1cm;
            size: A4;
          }
        }
      `}</style>
    </div>
  );
};

export default OrderDetailModal;
